import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ReportEntity, ReportSnapshotEntity, ResourcePermissionEngine } from '@memberjunction/core-entities';
import { SkipAPIAnalysisCompleteResponse } from '@memberjunction/skip-types';
import { InstanceProviderService } from '../instance-provider.service';
import { IMetadataProvider, RunView } from '@memberjunction/core';
import { ResourcePermissionsComponent } from '@memberjunction/ng-resource-permissions';
import { MJNotificationService } from '@memberjunction/ng-notifications';
import { SkipDynamicLinearReportComponent } from '@memberjunction/ng-skip-chat';
@Component({
  selector: 'app-single-report',
  templateUrl: './single-report.component.html',
  styleUrl: './single-report.component.css'
})
export class SingleReportComponent implements OnInit  {
  constructor(
    private route: ActivatedRoute,
    private instanceProvider: InstanceProviderService,
    private notificationService: MJNotificationService,
    private cdr: ChangeDetectorRef
  ) {}
 
  public theReport: ReportEntity;
  public skipData: SkipAPIAnalysisCompleteResponse;
  public reportResourceTypeID: string;
  public loading: boolean = true;
  public loadingFailed: boolean = false;  
  public snapshots: ReportSnapshotEntity[] = [];
  public activeSnapshot: ReportSnapshotEntity | null = null;  

  public userCanEdit: boolean = false;
  public userCanView: boolean = false;
  public userHasOwnership: boolean = false;
  public showRefreshHistoryPanel: boolean = false;

  @ViewChild('reportComponent') reportComponent: SkipDynamicLinearReportComponent;

  @ViewChild('resourcePermissions') set resourcePermissionsRef(
    component: ResourcePermissionsComponent | undefined
  ) {
    if (component) {
      // Component is instantiated
      this.resourcePermissions = component;
    } else {
      // Component is destroyed
      this.resourcePermissions = null;
    }
  }
  resourcePermissions: ResourcePermissionsComponent | null = null;

  ngOnInit(): void {
    this.instanceProvider.isInstanceLoaded$.subscribe(async (status: 'loading' | 'loaded' | 'failed') => {
      if (status === 'loaded') { 
        this.route.params.subscribe(params => {
          const id = params['id'];
          this.loadReport(id);
          this.reportResourceTypeID =  this.instanceProvider.ReportResourceType.ID;
        });
      }
      else if (status === 'failed') {
        this.loading = false;
        this.loadingFailed = true
      }
    });
  }

  public get Provider(): IMetadataProvider {
    return this.instanceProvider.InstanceProvider;
  }

  protected async loadReport(id: string) {
    this.loading = true;
    this.cdr.detectChanges(); // force a refresh after setting loading to true
    
    const p = this.instanceProvider.InstanceProvider;
    const reportEntity = await p.GetEntityObject<ReportEntity>("Reports", p.CurrentUser);
    const loadPromise = reportEntity.Load(id);

    // now load the report history
    const rv = this.instanceProvider.InstanceRunViewProvider;
    const runViewPromise = rv.RunView<ReportSnapshotEntity>({
      EntityName: "Report Snapshots",
      ExtraFilter: "ReportID = '" + id + "'", // only get the snapshots for this report
      OrderBy: "__mj_CreatedAt DESC",
    });

    // now await for both promises to complete
    const [, runViewResults] = await Promise.all([loadPromise, runViewPromise]);

    // Next up, permissions check
    if (p.CurrentUser.ID === reportEntity.UserID) {
      // the user is the creator of the report, so they have edit and ownership
      this.userCanEdit = true;
      this.userCanView = true;
      this.userHasOwnership = true;
    }
    else {
      // user is NOT the owner of the report, so let's see what sharing permissions they have - either by ROLE or by USER level resource sharing permissions
      // if the user doesn't have access at all, set that bit here, and if they do have rights, check to see if they are VIEW only, EDIT, or OWNER and use the highest
      // across the roles and the user level permissions
      const rpe = <ResourcePermissionEngine>ResourcePermissionEngine.GetProviderInstance(p, ResourcePermissionEngine);
      await rpe.Config(false, p.CurrentUser, p);

      // now get the permissions
      const highestLevel = rpe.GetUserResourcePermissionLevel(this.reportResourceTypeID, id, p.CurrentUser);

      // start off with these bits off
      this.userCanEdit = false; 
      this.userHasOwnership = false;
      this.userCanView = false;

      if (highestLevel === 'Owner') {
        this.userCanView = true;
        this.userCanEdit = true;
        this.userHasOwnership = true;
      }
      else if (highestLevel === 'Edit') {
        this.userCanEdit = true;
        this.userCanView = true;
      }
      else if (highestLevel === 'View') {
        this.userCanView = true;
      }
    }
    
    // if the user doesn't have at least view rights, we need to stop here and not load the report
    if (!this.userCanView) {
      this.loading = false;
      return;
    }

    // now get the report history from promise2 variable
    this.snapshots = runViewResults.Results;
    if (this.snapshots?.length > 0) {
      this.activeSnapshot = this.snapshots[0]; // select the most recent snapshot
    }

    this.skipData = JSON.parse(reportEntity.Configuration!);
    // set the explanation from the report itself though as that doesn't change
    this.skipData.userExplanation = reportEntity.Description;
    this.theReport = reportEntity;
    this.loading = false;
  }


  public showResourcePermissions: boolean = false;
  public doResourcePermissionsDialog() {
    this.showResourcePermissions = true;
  }

  public async closeResourcePermissionsDialog(action: 'yes' | 'no') {
    if (action === 'yes') {
      if (!await this.resourcePermissions.SavePermissions()) {
        // let the user know that sharing failed
        this.notificationService.CreateSimpleNotification('Failed to save permissions', 'error', 2500);
      }
      else {
        // let the user know that sharing was successful
        this.notificationService.CreateSimpleNotification('Report sharing settings updated', 'success', 1500);
      }
    }
    this.showResourcePermissions = false;
  }

  public async refreshReport() {
    this.loading = true;
    //this.cdr.detectChanges(); // force a refresh after setting loading to true to show the loader
    await this.reportComponent.DoRefreshReport();
    await this.loadReport(this.theReport.ID); // laod again to refresh the UI
    this.loading = false;
  }

  public selectSnapshot(snapshot: ReportSnapshotEntity) {
    this.activeSnapshot = snapshot;
    const json = JSON.parse(snapshot.ResultSet);
    // check to see if json is still a string or not, if it is and has length > 1 then parse it again
    if (typeof json === 'string' && json.length > 1) {
      this.skipData = JSON.parse(json);
    } else {
      this.skipData = json;
    }
    // set the explanation from the report itself though as that doesn't change
    this.skipData.userExplanation = this.theReport.Description;//reportJson.userExplanation;
  }

  public toggleRefreshHistoryPanel() {
    this.showRefreshHistoryPanel = !this.showRefreshHistoryPanel;
  }
}
